import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Bell } from 'lucide-react'
import { useAuth } from '../../../../app/modules/auth';
import { getConfigDatafromAPI, getNotifications } from '../../../../app/modules/core/_request';
import { ApiResponse, INotificationItem } from '../../../../app/modules/core/_models';
import { useSnackbar } from '../../../context/SnackbarContext';
import { Notification, NotificationFilter } from '../../../partials/layout/header-menus/notification';
import { HeaderUserMenu } from '../../../partials';
import { toAbsoluteUrl } from '../../../helpers';
import AppsLauncher from '../../../partials/layout/header-menus/AppsLauncher';
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";

const NOTIFICATION_PAGE_SIZE = 10;

const Topbar: FC = () => {
  const { showSnackbar } = useSnackbar()
  const { auth } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationData, setNotificationData] = useState<INotificationItem[]>([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationLoadingMore, setNotificationLoadingMore] = useState(false);
  const [notificationHasMore, setNotificationHasMore] = useState(true);
  const [starredCount, setStarredCount] = useState(0);
  const notificationRequestId = useRef(0);
  const configAbortRef = useRef<AbortController | null>(null);
  const notificationsAbortRef = useRef<AbortController | null>(null);

  const fetchNotifications = useCallback(
    async (
      page: number,
      append: boolean,
      filterType: NotificationFilter,
      importantOnly: boolean
    ) => {
      const requestId = ++notificationRequestId.current;
      notificationsAbortRef.current?.abort();
      const controller = new AbortController();
      notificationsAbortRef.current = controller;

      if (append) setNotificationLoadingMore(true);
      else setNotificationLoading(true);

      try {
        const response = await getNotifications({
          only_unread: 1,
          offset: (page - 1) * NOTIFICATION_PAGE_SIZE,
          limit: NOTIFICATION_PAGE_SIZE,
          notification_type: filterType === 'all' ? undefined : filterType,
          is_starred: importantOnly ? 1 : undefined,
        }, controller.signal);

        if (controller.signal.aborted || requestId !== notificationRequestId.current) {
          return [];
        }

        if (response.status !== 'ok') {
          throw new Error(
            response.message || 'Failed to fetch notifications'
          );
        }

        const list = response.data?.list || [];

        setNotificationData(previous =>
          append ? [...previous, ...list] : list
        );
        setNotificationHasMore(response.data?.has_more === true);
        setUnreadCount(response.data?.total ?? list.length);
        setStarredCount(response.data?.is_starred_count ?? 0);

        return list;
      } catch (error) {
        if (isAbortedError(error) || requestId !== notificationRequestId.current) {
          return [];
        }
        showSnackbar(getApiErrorMessage(error, "Failed to fetch notifications"), "danger");
        return [];
      } finally {
        if (requestId === notificationRequestId.current && !controller.signal.aborted) {
          setNotificationLoading(false);
          setNotificationLoadingMore(false);
        }
      }
    },
    [showSnackbar]
  );

  useEffect(() => {
    fetchNotifications(1, false, 'all', false);
    return () => {
      notificationsAbortRef.current?.abort();
    };
  }, [fetchNotifications]);

  const handleGetConfigData = useCallback(async () => {
    configAbortRef.current?.abort();
    const controller = new AbortController();
    configAbortRef.current = controller;

    try {
      const response = await getConfigDatafromAPI(controller.signal) as ApiResponse
      if (controller.signal.aborted) return;
      if (response.status === 'ok') {
        localStorage.setItem('config', JSON.stringify(response.data));
      } else {
        showSnackbar(response.message, 'danger');
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      showSnackbar(getApiErrorMessage(error, "Something went wrong"), "danger");
    }
  }, [showSnackbar])

  useEffect(() => {
    handleGetConfigData();
    return () => {
      configAbortRef.current?.abort();
    };
  }, [handleGetConfigData]);

  const handleCloseNotification = () => {
    const menuTrigger = document.getElementById('notification_menu_trigger');
    if (menuTrigger) {
      menuTrigger.click();
    }
  };

  const userMenuIcon = useMemo(() => (
    <div className="d-flex align-items-center justify-content-start flex-shrink-0">
      <img
        className="h-30px w-30px rounded flex-shrink-0"
        src={toAbsoluteUrl('media/avatars/blank.png')}
        alt="user avatar"
      />

      <span className="ms-2 d-none d-lg-inline text-white fs-5">
        {`${auth?.first_name?.charAt(0)}. ${auth?.last_name}`}
      </span>
    </div>
  ), [auth?.first_name, auth?.last_name]);

  return (
    <div className='d-flex align-items-stretch flex-shrink-0 gap-5'>
      {/* <CommonTopbarControls variant="private" /> */}
      <AppsLauncher />
      <div
        id="notification_menu_trigger"
        className="d-flex align-items-center flex-shrink-0 ms-1 ms-lg-3"
        aria-label="Open Notification"
      >
        <button
          type="button"
          aria-label="Open Notification"
          className="btn btn-link symbol-30px symbol-md-40px"
          data-kt-menu-trigger="click"
          data-kt-menu-attach="parent"
          data-kt-menu-placement="bottom-end"
        >
          <div className="position-relative d-inline-block">
            <Bell size={24} strokeWidth={1.5} color="white" />
            <span className="d-flex justify-content-center align-items-center position-absolute top-0 start-100 translate-middle badge text-white rounded-pill bg-danger ">
              {unreadCount}
            </span>
          </div>
        </button>

        <Notification
          onClose={handleCloseNotification}
          onUnreadCountChange={setUnreadCount}
          notificationData={notificationData}
          loading={notificationLoading}
          loadingMore={notificationLoadingMore}
          hasMore={notificationHasMore}
          fetchNotifications={fetchNotifications}
          unreadCount={unreadCount}
          starredCount={starredCount}
        />
      </div>
      {auth && (
        <div
          id="kt_header_user_menu_toggle"
          className="d-flex align-items-center flex-shrink-0"
          aria-label="Open user menu"
        >
          <button
            type="button"
            aria-label="Open user menu"
            className="btn btn-link symbol-30px symbol-md-40px p-0"
            data-kt-menu-trigger="click"
            data-kt-menu-attach="parent"
            data-kt-menu-placement="bottom-end"
          >
            {userMenuIcon}
          </button>

          <HeaderUserMenu />
        </div>
      )}


    </div>
  )
}

export { Topbar }
